import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Lock, ArrowLeft, Trash2, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";

import { RouteCard } from "@/components/RouteCard";
import { LoginModal } from "@/components/LoginModal";
import { useAuth } from "@/lib/authContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";

export default function MyRoutesPage() {
    const [, setLocation] = useLocation();
    const { isLoggedIn } = useAuth();
    const { t } = useI18n();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Redirect if not logged in
    useEffect(() => {
        if (!isLoggedIn) {
            setLocation("/routes");
        }
    }, [isLoggedIn, setLocation]);

    const { data: myRoutes = [], isLoading } = useQuery({
        queryKey: ['my-routes'],
        queryFn: async () => {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData.session?.access_token;
            if (!token) return [];
            const res = await fetch("/api/routes", {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) return [];
            return res.json();
        },
        enabled: isLoggedIn
    });

    const handleSaveRoute = () => {
        if (!isLoggedIn) {
            setShowLoginModal(true);
        }
    };

    const handleDeleteRoute = async (routeId: string) => {
        try {
            const { data } = await supabase.auth.getSession();
            const token = data.session?.access_token;
            if (!token) return;

            const res = await fetch(`/api/routes/${routeId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error(t("routeList.deleteFailed"));

            toast({
                title: t("routeList.deletedTitle"),
                description: t("routeList.deletedDesc"),
            });

            queryClient.invalidateQueries({ queryKey: ["my-routes"] });
            queryClient.invalidateQueries({ queryKey: ["/api/routes/public"] });
        } catch (error) {
            toast({
                title: t("routeList.deleteErrorTitle"),
                description: t("routeList.deleteErrorDesc"),
                variant: "destructive",
            });
        }
    };

    if (!isLoggedIn) {
        return null;
    }

    return (
        <div className="min-h-screen flex flex-col bg-background">
            

            <main className="flex-1 container mx-auto px-4 py-8">
                <div className="mb-8">
                    <Link href="/routes">
                        <Button variant="ghost" className="mb-4">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            {t("routeBuilder.backToRoutes")}
                        </Button>
                    </Link>

                    <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                        <Lock className="h-6 w-6 text-primary" />
                        {t("routes.mine")}
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        {t("routeList.inYourOverview", { count: myRoutes.length })}
                    </p>
                </div>

                {isLoading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="h-[300px] bg-muted/20 animate-pulse rounded-lg" />
                        ))}
                    </div>
                ) : myRoutes.length === 0 ? (
                    <div className="text-center py-16">
                        <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="font-semibold text-lg mb-2">{t("routeList.noRoutesFound")}</h3>
                        <p className="text-muted-foreground mb-4">
                            {t("routeList.noMyRoutes")}
                        </p>
                        <Link href="/routes/new">
                            <Button>
                                {t("routeList.makeFirstRoute")}
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {myRoutes.map((route: any) => (
                            <RouteCard
                                key={route.id}
                                route={route}
                                onSave={handleSaveRoute}
                                onDelete={handleDeleteRoute}
                            />
                        ))}
                    </div>
                )}
            </main>

            <LoginModal
                open={showLoginModal}
                onOpenChange={setShowLoginModal}
                action="route"
            />
        </div>
    );
}
